import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Search, Download, Activity } from 'lucide-react';
import { supabase } from '../lib/supabase';
import CollaborationTable, { SortField, SortDirection } from '../components/market-watch/CollaborationTable';
import MarketFilters, { FilterValues } from '../components/market-watch/MarketFilters';
import CollaborationDrawer from '../components/market-watch/CollaborationDrawer';
import { estimateCollaborationPrice, detectBrandFromCaption, getContentTypeFromPost, isGenericBrandName } from '../utils/priceEstimation';

type Post = {
  id: string;
  influencer_id: string;
  post_id: string;
  post_url: string;
  caption: string;
  image_url: string;
  likes_count: number;
  comments_count: number;
  timestamp: string;
  is_collaboration: boolean;
  collaboration_keywords: string[];
  post_type?: string;
  scraped_at: string;
  influencer?: {
    id: string;
    display_name: string;
    full_name: string;
    username: string;
    profile_image_url: string;
    followers_count: number;
    category: string;
  };
};

interface Collaboration {
  id: string;
  creator_name: string;
  creator_handle: string;
  creator_avatar: string;
  followers: number;
  engagement_rate: number;
  post_engagement_rate: number;
  brand_name: string;
  platform: string;
  content_type: string;
  date_detected: string;
  estimated_price_min: number;
  estimated_price_max: number;
  confidence_score: 'high' | 'medium' | 'low';
  post_image_url: string;
  caption: string;
  post_url: string;
  category?: string;
}

const PAGE_SIZE = 50;

export default function MarketWatch() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [sortField, setSortField] = useState<SortField | null>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [filters, setFilters] = useState<FilterValues>({
    platform: [],
    dateRange: '30',
    minFollowers: null,
    maxFollowers: null,
    category: '',
    minPrice: null,
    maxPrice: null,
  });

  useEffect(() => {
    loadCollaborations();
  }, [page, search, filters]);

  const loadCollaborations = async () => {
    try {
      setIsLoading(true);

      let query = supabase
        .from('posts')
        .select('*', { count: 'exact' })
        .eq('is_collaboration', true)
        .order('timestamp', { ascending: false });

      if (filters.dateRange !== 'all') {
        const daysAgo = parseInt(filters.dateRange);
        const dateThreshold = new Date();
        dateThreshold.setDate(dateThreshold.getDate() - daysAgo);
        query = query.gte('timestamp', dateThreshold.toISOString());
      }

      if (search.trim()) {
        query = query.or(`caption.ilike.%${search}%,collaboration_keywords.cs.{${search}}`);
      }

      const { data: postsData, error: postsError, count } = await query;

      if (postsError) throw postsError;

      setTotalCount(count || 0);

      if (postsData && postsData.length > 0) {
        const influencerIds = [...new Set(postsData.map(p => p.influencer_id))];

        const { data: influencersData, error: influencersError } = await supabase
          .from('influencers')
          .select('id, display_name, full_name, username, profile_image_url, followers_count, category, engagement_rate')
          .in('id', influencerIds);

        if (influencersError) throw influencersError;

        const influencersMap = new Map(
          (influencersData || []).map(inf => [inf.id, inf])
        );

        const enrichedPosts = postsData
          .map(post => ({
            ...post,
            influencer: influencersMap.get(post.influencer_id)
          }))
          .filter(post => post.influencer);

        setPosts(enrichedPosts);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error loading collaborations:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const allCollaborations = useMemo(() => {
    return posts
      .filter(post => post.influencer)
      .map(post => {
        const influencer = post.influencer!;
        const followers = influencer.followers_count || 0;

        const engagement_rate = (influencer as any).engagement_rate || (
          followers > 0
            ? ((post.likes_count + post.comments_count) / followers) * 100
            : 0
        );

        const post_engagement_rate = followers > 0
          ? ((post.likes_count + post.comments_count) / followers) * 100
          : 0;

        const platform = 'Instagram';
        const content_type = getContentTypeFromPost(post);

        const priceEstimation = estimateCollaborationPrice(
          followers,
          engagement_rate,
          platform,
          content_type
        );

        const detectedBrand = detectBrandFromCaption(post.caption);

        let brand_name = 'Marque non identifiée';

        if (detectedBrand && !isGenericBrandName(detectedBrand)) {
          brand_name = detectedBrand;
        } else if (post.collaboration_keywords && post.collaboration_keywords.length > 0) {
          const validKeywords = post.collaboration_keywords.filter(
            (keyword: string) => !isGenericBrandName(keyword)
          );
          if (validKeywords.length > 0) {
            brand_name = validKeywords[0];
          }
        }

        return {
          id: post.id,
          creator_name: influencer.display_name || influencer.full_name,
          creator_handle: influencer.username,
          creator_avatar: influencer.profile_image_url,
          followers,
          engagement_rate,
          post_engagement_rate,
          brand_name,
          platform,
          content_type,
          date_detected: post.timestamp,
          estimated_price_min: priceEstimation.min,
          estimated_price_max: priceEstimation.max,
          confidence_score: priceEstimation.confidence,
          post_image_url: post.image_url,
          caption: post.caption,
          post_url: post.post_url,
          category: influencer.category,
        };
      });
  }, [posts]);

  const filteredAndSorted = useMemo(() => {
    let filtered = allCollaborations.filter(collab => {
      if (filters.platform.length > 0 && !filters.platform.includes(collab.platform)) {
        return false;
      }
      if (filters.minFollowers !== null && collab.followers < filters.minFollowers) {
        return false;
      }
      if (filters.maxFollowers !== null && collab.followers > filters.maxFollowers) {
        return false;
      }
      if (filters.minPrice !== null && collab.estimated_price_min < filters.minPrice) {
        return false;
      }
      if (filters.maxPrice !== null && collab.estimated_price_max > filters.maxPrice) {
        return false;
      }
      if (filters.category && collab.category !== filters.category) {
        return false;
      }
      return true;
    });

    if (sortField) {
      filtered.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortField) {
          case 'date':
            aVal = new Date(a.date_detected).getTime();
            bVal = new Date(b.date_detected).getTime();
            break;
          case 'followers':
            aVal = a.followers;
            bVal = b.followers;
            break;
          case 'engagement':
            aVal = a.engagement_rate;
            bVal = b.engagement_rate;
            break;
          case 'price':
            aVal = a.estimated_price_min;
            bVal = b.estimated_price_min;
            break;
          default:
            return 0;
        }

        if (sortDirection === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });
    }

    return filtered;
  }, [allCollaborations, filters, sortField, sortDirection]);

  const collaborations = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredAndSorted.slice(start, end);
  }, [filteredAndSorted, page]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleRowClick = (collaboration: Collaboration) => {
    setSelectedCollaboration(collaboration);
    setIsDrawerOpen(true);
  };

  const handleExportCSV = () => {
    const headers = [
      'Date',
      'Créateur',
      'Handle',
      'Followers',
      'Marque',
      'Plateforme',
      'Type',
      'Engagement (%)',
      'Prix Min (€)',
      'Prix Max (€)',
      'Score',
    ];

    const rows = collaborations.map(c => [
      new Date(c.date_detected).toLocaleDateString('fr-FR'),
      c.creator_name,
      c.creator_handle,
      c.followers,
      c.brand_name,
      c.platform,
      c.content_type,
      c.engagement_rate.toFixed(2),
      c.estimated_price_min,
      c.estimated_price_max,
      c.confidence_score,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `market-watch-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const totalPages = Math.ceil(filteredAndSorted.length / PAGE_SIZE);
  const displayedCount = filteredAndSorted.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Market Watch</h1>
            <p className="text-white/50 mt-1">
              Suivez toutes les collaborations détectées entre marques et créateurs
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0">
            <Activity className="w-6 h-6 text-white/60" />
          </div>
          <div>
            <div className="text-3xl font-bold text-white">
              {displayedCount.toLocaleString('fr-FR')}
            </div>
            <div className="text-white/40 text-sm">
              collaborations détectées sur {filters.dateRange === 'all' ? 'tous les temps' : `les ${filters.dateRange} derniers jours`}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-6">
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Rechercher un créateur, une marque ou un mot-clé..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
            />
          </div>
          <div className="flex gap-3">
            <MarketFilters filters={filters} onFiltersChange={setFilters} />
            <button
              onClick={handleExportCSV}
              disabled={collaborations.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.08] hover:bg-white/[0.14] disabled:opacity-40 disabled:cursor-not-allowed text-white/60 border border-white/[0.08] rounded-xl font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-white/60"></div>
          </div>
        ) : collaborations.length === 0 ? (
          <div className="text-center py-24 bg-white/[0.04] rounded-xl border border-dashed border-white/[0.08]">
            <div className="w-16 h-16 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-white/30" />
            </div>
            <p className="text-white/50 text-lg font-medium">
              Aucune collaboration détectée pour les critères sélectionnés
            </p>
            <p className="text-white/30 text-sm mt-2">
              Essayez d'ajuster vos filtres ou votre recherche
            </p>
          </div>
        ) : (
          <>
            <CollaborationTable
              collaborations={collaborations}
              onRowClick={handleRowClick}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-white/[0.06]">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 rounded-xl border font-medium transition-colors ${
                    page === 1
                      ? 'opacity-40 cursor-not-allowed bg-white/[0.04] border-white/[0.08] text-white/30'
                      : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
                  }`}
                >
                  Précédent
                </button>

                <span className="text-white/50 font-medium px-4">
                  Page {page} sur {totalPages}
                </span>

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 rounded-xl border font-medium transition-colors ${
                    page === totalPages
                      ? 'opacity-40 cursor-not-allowed bg-white/[0.04] border-white/[0.08] text-white/30'
                      : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
                  }`}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <CollaborationDrawer
        collaboration={selectedCollaboration}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
}
